import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { FormattedAsset } from "@/types/asset";
import type { AssetType } from "@/types/asset";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

// Map display names to AssetType enum values
const PROPERTY_TYPES: Record<string, AssetType> = {
  "Single Family": "single_family",
  Condominium: "condominium",
  "Multi-Family": "multi_family",
  Commercial: "commercial",
  Industrial: "industrial",
  Agricultural: "agricultural",
  "Vacant Land": "vacant_land",
  "Mixed-Use": "mixed_use",
  "Public/Institutional": "public_institutional",
  Recreational: "recreational",
  Transportation: "transportation",
};

// Define form schema using zod
const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  address: z.string().optional(),
  asset_type: z.enum([
    "single_family",
    "condominium",
    "multi_family",
    "commercial",
    "industrial",
    "agricultural",
    "vacant_land",
    "mixed_use",
    "public_institutional",
    "recreational",
    "transportation",
  ]),
  year_built: z.number().nullable().optional(),
  area_square_feet: z.number().nullable().optional(),
  beds: z.number().nullable().optional(),
  baths: z.number().nullable().optional(),
  stories: z.number().nullable().optional(),
  units: z.number().nullable().optional(),
  lot_size_square_feet: z.number().nullable().optional(),
  purchase_price: z.number().nullable().optional(),
  purchase_date: z.string().nullable().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface PropertyDetailsStepProps {
  assetData: FormattedAsset | null;
  onNext: (data: FormValues) => void;
  onBack: () => void;
  isLoading: boolean;
}

const PropertyDetailsStep = ({
  assetData,
  onNext,
  onBack,
  isLoading,
}: PropertyDetailsStepProps) => {
  // Setup form with React Hook Form
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      address: "",
      asset_type: "single_family",
      year_built: null,
      area_square_feet: null,
      beds: null,
      baths: null,
      stories: null,
      units: 1,
      lot_size_square_feet: null,
      purchase_price: null,
      purchase_date: null,
    },
  });

  // Get current asset type value
  const currentAssetType = form.watch("asset_type");

  // Check if the current property type should show a unit dropdown
  const shouldShowUnitDropdown = [
    "multi_family",
    "commercial",
    "condominium",
  ].includes(currentAssetType);

  // Generate unit options for dropdown (1-50 units for better coverage)
  const unitOptions = Array.from({ length: 50 }, (_, i) => i + 1);

  // Populate form fields with asset data when available
  useEffect(() => {
    if (assetData) {
      // Map FormattedAsset fields to form fields
      form.setValue("name", assetData.asset_name || "");
      form.setValue("address", assetData.address?.full || "");

      // Handle asset_type mapping - convert from string to enum value if needed
      if (assetData.asset_type) {
        // First check if it's already a valid enum value (exact match)
        if (
          Object.values(PROPERTY_TYPES).includes(
            assetData.asset_type as AssetType
          )
        ) {
          form.setValue("asset_type", assetData.asset_type as AssetType);
        }
        // Check if it's a display name (like "Condominium") rather than an enum value
        else if (PROPERTY_TYPES[assetData.asset_type]) {
          form.setValue("asset_type", PROPERTY_TYPES[assetData.asset_type]);
        }
        // Try to match it case-insensitively
        else {
          const assetTypeLower = assetData.asset_type.toLowerCase();

          // Check enum values case-insensitively
          const matchingEnumValue = Object.values(PROPERTY_TYPES).find(
            (enumValue) => enumValue.toLowerCase() === assetTypeLower
          );

          if (matchingEnumValue) {
            form.setValue("asset_type", matchingEnumValue);
          }
          // Check display names case-insensitively
          else {
            const matchingDisplayName = Object.keys(PROPERTY_TYPES).find(
              (displayName) => displayName.toLowerCase() === assetTypeLower
            );

            if (matchingDisplayName) {
              form.setValue("asset_type", PROPERTY_TYPES[matchingDisplayName]);
            } else {
              form.setValue("asset_type", "single_family");
            }
          }
        }
      } else {
        form.setValue("asset_type", "single_family");
      }

      form.setValue("year_built", assetData.yearBuilt || null);
      form.setValue("area_square_feet", assetData.areaSquareFeet || null);
      form.setValue("beds", assetData.beds || null);
      form.setValue("baths", assetData.baths || null);
      form.setValue("units", assetData.units || 1);
      form.setValue("stories", assetData.stories || null);
      form.setValue(
        "lot_size_square_feet",
        assetData.lotSizeSquareFeet || null
      );

      form.setValue("purchase_price", null);
      form.setValue("purchase_date", null);
    }
  }, [assetData, form]);

  const getDisplayNameFromEnum = (enumValue: AssetType): string => {
    const entry = Object.entries(PROPERTY_TYPES).find(
      ([_, value]) => value === enumValue
    );
    return entry ? entry[0] : "Single Family";
  };

  const onSubmit = (data: FormValues) => {
    onNext(data);
  };

  return (
    <Form {...form}>
      <Card>
        <CardHeader>
          <CardTitle>Property Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Property Name</FormLabel>
                <FormControl>
                  <Input placeholder="Enter property name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="address"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Address</FormLabel>
                <FormControl>
                  <Input placeholder="Enter address" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="asset_type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Property Type</FormLabel>
                <Select
                  onValueChange={(value) => {
                    const enumValue = PROPERTY_TYPES[value];
                    field.onChange(enumValue);
                  }}
                  value={getDisplayNameFromEnum(field.value as AssetType)}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a property type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {Object.keys(PROPERTY_TYPES).map((displayName) => (
                      <SelectItem key={displayName} value={displayName}>
                        {displayName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="year_built"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Year Built</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="Year built"
                      value={field.value || ""}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value ? Number(e.target.value) : null
                        )
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="area_square_feet"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Square Footage</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="Area in sq. ft."
                      value={field.value || ""}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value ? Number(e.target.value) : null
                        )
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="beds"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bedrooms</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="Number of bedrooms"
                      value={field.value || ""}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value ? Number(e.target.value) : null
                        )
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="baths"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bathrooms</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="Number of bathrooms"
                      value={field.value || ""}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value ? Number(e.target.value) : null
                        )
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="stories"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Stories</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="Number of stories"
                      value={field.value || ""}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value ? Number(e.target.value) : null
                        )
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="units"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Number of Units
                    {shouldShowUnitDropdown && (
                      <span className="text-muted-foreground font-normal ml-1">
                        (Multi-unit property)
                      </span>
                    )}
                  </FormLabel>
                  <FormControl>
                    {shouldShowUnitDropdown ? (
                      <Select
                        onValueChange={(value) => field.onChange(Number(value))}
                        value={field.value?.toString() || "1"}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select number of units" />
                        </SelectTrigger>
                        <SelectContent>
                          {unitOptions.map((unit) => (
                            <SelectItem key={unit} value={unit.toString()}>
                              {unit} {unit === 1 ? "unit" : "units"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        type="number"
                        min="1"
                        placeholder="Number of units (1 for single unit)"
                        value={field.value || ""}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value ? Number(e.target.value) : null
                          )
                        }
                      />
                    )}
                  </FormControl>
                  <p className="text-xs text-muted-foreground">
                    {shouldShowUnitDropdown
                      ? "Specify the total number of units. You can tag transactions with specific unit numbers when adding them."
                      : "Enter 1 for single-family properties, or the number of units for multi-unit properties. You can tag transactions with specific unit numbers when adding them."}
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="lot_size_square_feet"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Lot Size (sq ft)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="Lot size in sq. ft."
                      value={field.value || ""}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value ? Number(e.target.value) : null
                        )
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between mt-6">
        <Button type="button" variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button
          type="button"
          disabled={isLoading}
          onClick={() => form.handleSubmit(onSubmit)()}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            "Next"
          )}
        </Button>
      </div>
    </Form>
  );
};

export default PropertyDetailsStep;
